import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import Text from '../components/shared/Text';

function ReturnOrderShow() {
    const { id } = useParams();
    const [returnOrder, setReturnOrder] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadReturnOrder();
    }, [id]);

    const loadReturnOrder = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/return_orders/${id}`);
            if (response.success) {
                setReturnOrder(response.data);
            } else {
                setError(response.message || 'Failed to load return');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return 'bg-emerald-100 text-emerald-700';
            case 'pending': return 'bg-amber-100 text-amber-700';
            case 'rejected': return 'bg-rose-100 text-rose-700';
            case 'completed': return 'bg-blue-100 text-blue-700';
            default: return 'bg-slate-100 text-slate-700';
        }
    };

    const getConditionColor = (condition: string) => {
        switch (condition) {
            case 'resellable': return 'bg-emerald-100 text-emerald-700';
            case 'damaged': return 'bg-rose-100 text-rose-700';
            case 'defective': return 'bg-amber-100 text-amber-700';
            default: return 'bg-slate-100 text-slate-700';
        }
    };

    if (loading) {
        return (
            <div className="mt-16 p-8 text-center">
                <Text variant="body" color="slate" weight="medium">Loading return...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-16 p-8 text-center">
                <Text variant="body" weight="medium" className="text-red-500">{error}</Text>
            </div>
        );
    }

    if (!returnOrder) {
        return (
            <div className="mt-16 p-8 text-center">
                <Text variant="body" color="slate" weight="medium">Return not found</Text>
            </div>
        );
    }

    return (
        <div className="mt-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3">
                        <Link to="/returns" className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </Link>
                        <Text variant="h1" color="primary">Return {returnOrder.return_number}</Text>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(returnOrder.status)}`}>
                            {returnOrder.status?.toUpperCase()}
                        </span>
                    </div>
                    <div className="mt-1 ml-11">
                        <Text variant="body" color="slate">
                            Original Invoice: <Link to={`/invoices/${returnOrder.invoice_id}`}>
                                <Text weight="medium" className="text-indigo-600 hover:text-indigo-700">{returnOrder.invoice_number}</Text>
                            </Link>
                        </Text>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    {/* Items Table */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200">
                            <Text variant="h3" color="primary">Returned Items</Text>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left"><Text variant="label" color="slate">Product</Text></th>
                                        <th className="px-6 py-3 text-center"><Text variant="label" color="slate">Qty</Text></th>
                                        <th className="px-6 py-3 text-center"><Text variant="label" color="slate">Condition</Text></th>
                                        <th className="px-6 py-3 text-right"><Text variant="label" color="slate">Price</Text></th>
                                        <th className="px-6 py-3 text-right"><Text variant="label" color="slate">Total</Text></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {returnOrder.items?.map((item: any) => (
                                        <tr key={item.id}>
                                            <td className="px-6 py-4">
                                                <Text variant="body" weight="medium" color="primary">{item.product_name}</Text>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <Text variant="body" color="primary">{item.quantity}</Text>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getConditionColor(item.condition)}`}>
                                                    {item.condition?.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Text variant="mono" color="primary" className="text-sm">₹{item.unit_price?.toLocaleString()}</Text>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Text variant="mono" weight="bold" color="primary">₹{item.total_amount?.toLocaleString()}</Text>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Notes */}
                    {returnOrder.notes && (
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                            <Text variant="h3" color="primary" className="mb-4">Notes</Text>
                            <Text variant="body" color="slate" className="whitespace-pre-wrap">{returnOrder.notes}</Text>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <Text variant="h3" color="primary" className="mb-6">Return Summary</Text>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <Text variant="small" color="slate">Total Amount</Text>
                                <Text variant="small" weight="medium" color="primary">₹{returnOrder.total_amount?.toLocaleString()}</Text>
                            </div>
                            <div className="pt-4 border-t border-slate-200 flex justify-between items-center">
                                <Text variant="h3" color="primary">Refund Amount</Text>
                                <Text variant="h3" className="text-emerald-600 font-mono">₹{returnOrder.refund_amount?.toLocaleString()}</Text>
                            </div>
                            <div className="flex justify-between items-center">
                                <Text variant="small" color="slate">Refund Method</Text>
                                <Text variant="small" weight="medium" color="primary" className="capitalize">{returnOrder.refund_method?.replace('_', ' ')}</Text>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <Text variant="h3" color="primary" className="mb-4">Return Info</Text>
                        <div className="space-y-4">
                            <div>
                                <Text variant="label" color="slate" className="mb-1 block">Customer</Text>
                                <Text variant="body" weight="medium" color="primary">{returnOrder.customer_name}</Text>
                            </div>
                            <div>
                                <Text variant="label" color="slate" className="mb-1 block">Return Date</Text>
                                <Text variant="body" weight="medium" color="primary">{new Date(returnOrder.return_date).toLocaleDateString(undefined, { dateStyle: 'long' })}</Text>
                            </div>
                            <div>
                                <Text variant="label" color="slate" className="mb-1 block">Reason</Text>
                                <Text variant="body" weight="medium" color="primary" className="capitalize">{returnOrder.reason?.replace('_', ' ')}</Text>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReturnOrderShow;
